/**
 * ReadingProgress — top-of-page progress bar that fills as the reader
 * scrolls through a blog post.
 *
 * Tracks `window.scrollY / (documentHeight - viewport)` and renders
 * a `[role="progressbar"]` ARIA-friendly element. Listens to the
 * scroll event with a passive listener for performance.
 *
 * @module @puffin-core/marketing-shell/blog/ReadingProgress
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

export default function ReadingProgress({ ariaLabel, scrollContainer }) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    // No `typeof window === 'undefined'` guard: a `useEffect` callback only
    // ever runs client-side (React never invokes effects during SSR /
    // renderToString), so `window` is always defined here.
    const target = scrollContainer || window;

    function measure() {
      const scrollTop = target === window ? window.scrollY : target.scrollTop || 0;
      const docHeight =
        target === window
          ? document.documentElement.scrollHeight - window.innerHeight
          : target.scrollHeight - target.clientHeight;
      if (docHeight <= 0) {
        setPct(0);
        return;
      }
      const ratio = scrollTop / docHeight;
      const clamped = Math.max(0, Math.min(1, ratio));
      setPct(Math.round(clamped * 100));
    }
    measure();

    const opts = target === window ? { passive: true } : { passive: true };
    target.addEventListener('scroll', measure, opts);
    target.addEventListener('resize', measure);
    return () => {
      target.removeEventListener('scroll', measure);
      target.removeEventListener('resize', measure);
    };
  }, [scrollContainer]);

  // Per CSS-001: no inline `style={{ width: ... }}`. Instead, set a
  // CSS custom property the canonical `.post-reading-progress-bar`
  // utility class consumes via `width: calc(var(--reading-progress) * 1%)`.
  // The custom-property write is the only DOM-level value the progress
  // bar carries between renders; the actual `width` rule lives in the
  // shared-styles utility.
  //
  // The bar's accessible name: the host's, else the catalog's in the reader's language — never an
  // English default parameter.
  const t = useMarketingShellTranslate();
  return (
    <div
      className="post-reading-progress"
      role="progressbar"
      aria-label={ariaLabel ?? t('readingProgress.label')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
    >
      <div className="post-reading-progress-bar" style={{ '--reading-progress': pct }} />
    </div>
  );
}

ReadingProgress.propTypes = {
  ariaLabel: PropTypes.string,
  scrollContainer: PropTypes.any,
};
